'use client';

import { useCallback, useMemo } from 'react';

import { useRedirect } from 'hooks/use-redirect';

import { hardNavigate, toAbsoluteUrl } from '@login/helpers/navigation';

type IdmAction = 'Redirect' | 'Display';

type ApiRedirectProps = {
	action: IdmAction;
	url?: string | null;
};

/** IdentityServer endpoints, served by the backend rather than by Next. */
const EXTERNAL_PATH_PREFIX = '/connect';

export const isExternalIdmPath = (url: string) =>
	url.startsWith(EXTERNAL_PATH_PREFIX);

export const useApiRedirect = () => {
	const redirect = useRedirect();

	const goToRedirect = useCallback(
		({ url, action }: ApiRedirectProps) => {
			if (action !== 'Redirect' || !url) return;

			// `/connect/*` is not a Next route, so it needs a document
			// navigation rather than a client-side push. Every account form
			// used to inline this branch; it now lives here only.
			if (isExternalIdmPath(url)) {
				hardNavigate(toAbsoluteUrl(url));
				return;
			}

			redirect({ path: url });
		},
		[redirect]
	);

	return useMemo(() => ({ goToRedirect }), [goToRedirect]);
};

export default useApiRedirect;
